import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Copy, Check, Package, Link as LinkIcon, Loader, MessageSquare, Inbox } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Dashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  const fetchPurchases = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/purchases`, { withCredentials: true });
      setPurchases(data);
    } catch (error) {
      console.error('Failed to fetch purchases:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && (!user || user === false)) {
      navigate('/login');
    } else if (user) {
      fetchPurchases();
    }
  }, [user, authLoading, navigate, fetchPurchases]);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center" data-testid="dashboard-loading">
        <Loader className="w-7 h-7 accent-green animate-spin" />
      </div>
    );
  }

  const firstName = user?.name?.split(' ')[0] || 'there';

  return (
    <div className="min-h-[calc(100vh-80px)]" data-testid="dashboard-page">
      <section className="relative py-16 px-4 sm:px-6 lg:px-8 hero-gradient overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <span className="eyebrow">Welcome Back</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mt-3 mb-3 text-strong tracking-tight" data-testid="dashboard-welcome">
            Hello, <span className="text-gradient-brand">{firstName}.</span>
          </h1>
          <p className="text-base text-muted max-w-2xl">
            Your private control room. Manage your widgets, grab OBS links, and request new builds — all in one place.
          </p>
          {user?.role === 'admin' && (
            <a
              href="/admin/contacts"
              className="mt-6 inline-flex items-center space-x-2 btn-secondary px-4 py-2 text-sm"
              data-testid="dashboard-admin-inbox-link"
            >
              <Inbox className="w-4 h-4" />
              <span>Admin Inbox · Contact Submissions</span>
            </a>
          )}
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 border-t border-subtle">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div>
              <span className="eyebrow eyebrow-blue">Your Collection</span>
              <h2 className="text-2xl sm:text-3xl font-bold mt-2 text-strong" data-testid="dashboard-heading">
                My Widgets
              </h2>
            </div>
            <a href="/#contact" className="hidden sm:inline-flex btn-secondary px-5 py-2.5 items-center space-x-2 text-sm"
              data-testid="dashboard-request-button">
              <MessageSquare className="w-4 h-4" />
              <span>Request New Widget</span>
            </a>
          </div>

          {purchases.length === 0 ? (
            <div className="text-center py-20 card-surface" data-testid="dashboard-empty">
              <Package className="w-12 h-12 text-faded mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2 text-strong">No widgets yet</h3>
              <p className="text-muted mb-6 text-sm">Get in touch and we'll set you up with your first widget.</p>
              <a href="/#contact" className="btn-primary px-6 py-3 inline-block" data-testid="dashboard-contact-button">
                Request a Widget
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {purchases.map((purchase) => (
                <div
                  key={purchase.widget_id}
                  className="widget-card"
                  data-testid={`dashboard-widget-${purchase.widget_id}`}
                >
                  {purchase.widget && (
                    <>
                      <div className="aspect-video bg-[#0F1113] overflow-hidden">
                        <img src={purchase.widget.image_url} alt={purchase.widget.name}
                          className="w-full h-full object-cover"
                          data-testid={`dashboard-widget-image-${purchase.widget_id}`} />
                      </div>
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-base font-semibold text-strong" data-testid={`dashboard-widget-name-${purchase.widget_id}`}>
                            {purchase.widget.name}
                          </h3>
                          <span className={`text-[10px] px-2 py-1 rounded-md uppercase tracking-widest font-bold border ${
                            purchase.status === 'active'
                              ? 'border-[#39FF14]/30 text-[#39FF14] bg-[#39FF14]/8'
                              : 'border-yellow-500/30 text-yellow-400 bg-yellow-500/8'
                          }`}>
                            {purchase.status}
                          </span>
                        </div>

                        {purchase.status === 'active' && purchase.obs_link && (
                          <div className="space-y-3">
                            <label className="block text-[10px] font-semibold text-faded uppercase tracking-widest">
                              OBS Browser Source URL
                            </label>
                            <div className="flex items-center space-x-2">
                              <div className="flex-1 surface-2 border border-subtle rounded-lg px-3 py-2 text-xs text-default font-mono overflow-hidden text-ellipsis whitespace-nowrap">
                                {purchase.obs_link}
                              </div>
                              <button
                                onClick={() => copyToClipboard(purchase.obs_link, purchase.widget_id)}
                                className="btn-primary p-2"
                                data-testid={`copy-obs-link-button-${purchase.widget_id}`}
                              >
                                {copiedId === purchase.widget_id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                              </button>
                            </div>
                            <p className="text-[11px] text-muted flex items-start space-x-1.5">
                              <LinkIcon className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span>Add this URL as a Browser Source in OBS</span>
                            </p>
                          </div>
                        )}

                        {purchase.status === 'pending' && (
                          <div className="p-3 rounded-lg border border-yellow-500/30 bg-yellow-500/5">
                            <p className="text-xs text-yellow-400">
                              Your request is pending. We'll be in touch soon to finalize.
                            </p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
